import {Vector3} from 'three';
import create from 'zustand'
import {JazeemInventoryEvent} from "src/API";
import {
  arrangeEarthClusterTiles,
  cullOldTiles,
  generateSpiralLerp,
  getEntranceWaypoint,
  TileZone
} from "src/arrangement/fulfillmentZones";
import {IN_STORE, LIVE} from "src/globalVariables/globalVariables";
import {StateCode} from "src/components/3d/USAMap";
import {NamedCluster, storeLocations} from "src/stores/storeLocations";
import {Parametric} from "src/components/3d/AsinTile";
import {clusterWorldPositions} from "src/components/3d/FreshStores";

export interface AsinTileData {
  asin: string;
  tileId: string;
  storeId: string;
  storeBrandName?: string;
  customerId: string;
  liveOrReplay: string;
  fulfillmentOrderId?: string;
  checkedOut?: string;

  // We will intentionally avoid re-making this object because the data inside it is polled
  // in the AsinTile's useFrame loop; no need to re-render the AsinTile react component.
  animationData: AnimationData;

  dateAdded: Date;
}

export interface AnimationData {
  tileZone: TileZone;
  stateCode?: StateCode;
  cluster?: NamedCluster;
  destinationId: string;
  targetPosition?: Vector3;
  parametricFunction?: Parametric;
  parametricCurrentValue?: number;
  parametricTargetValue?: number;
  parametricOffset?: number;
  phase?: Phase;
  debutTime: Date;
  transition?: {
    transitioningTo: TileZone;
    startTime: Date;
    jitter: number;
    entranceWaypoint?: Vector3;
  }
}
export enum Store {
  All = 'All' ,
  'US-CA-164' = 'US-CA-164',
  'US-OH-19' = 'US-OH-19',
}

export type Phase = 'ENTRANCE' | 'FLY' | 'PARAMETRIC';

export interface KSavvyState {
  asinTiles: AsinTileData[];
  selectedStore: Store;
  focusCustomer: string;
  focusASIN: string;
  replayFilter: string;
  showFreshEvents: boolean;
  showWholeFoodsEvents: boolean;
  setShowFreshEvents: (showFreshEvents: boolean) => void;
  setShowWholeFoodsEvents: (showWholeFoodsEvents: boolean) => void;
  setSelectedStore: (store: Store) => void;
  setFocusCustomer: (customerId: string) => void;
  setFocusASIN: (asin: string) => void;
  setReplayFilter: (filter: string) => void;
  addInventoryEvent: (event: JazeemInventoryEvent, filter: string) => void,
  setTiles: (tiles: AsinTileData[]) => void;
}

const useStore = create<KSavvyState>((set, get) => {
  return {
    asinTiles: [],
    selectedStore: Store.All,
    focusCustomer: '',
    focusASIN: '',
    replayFilter: LIVE,
    showFreshEvents: true,
    showWholeFoodsEvents: false,
    setSelectedStore(store: Store) {
      set((state) => {
        return { selectedStore: store };
      })
    },
    setFocusCustomer(customerId: string) {
      set((state) => {
        return { focusCustomer: customerId };
      })
    },
    setFocusASIN(asin: string){
      set((state) => {
        return { focusASIN: asin };
      })
    },
    setReplayFilter(filter: string){
      set((state) => {
        return { replayFilter: filter };
      })
    },
    setTiles(tiles: AsinTileData[]){
      set(() => {
        return { asinTiles: tiles };
      })
    },
    setShowFreshEvents(showFreshEvents: boolean){
      set((state) => {
        return { showFreshEvents: showFreshEvents };
      })
    },
    setShowWholeFoodsEvents(showWholeFoodsEvents: boolean){
      set((state) => {
        return { showWholeFoodsEvents: showWholeFoodsEvents };
      })
    },
    addInventoryEvent(event: JazeemInventoryEvent, filter: string) {
      set((state) => {
        if (state.asinTiles.find((t) => t.tileId === event.eventId)) {
          return { asinTiles: state.asinTiles }; // No change, eventId is already here.
        }

        const cluster = storeLocations.stores[event.storeId]?.cluster;

        if (!cluster) {
          return { asinTiles: state.asinTiles };
        }

        const clusterPosition = clusterWorldPositions[cluster.name];

        const stateCode = event.storeId.split('-')[1];
        const debutBufferSeconds = 5;

        const tileData: AsinTileData = {
          asin: event.asin,
          tileId: event.eventId,
          storeId: event.storeId,
          storeBrandName: storeLocations.stores[event.storeId]?.brandName,
          customerId: "",
          liveOrReplay: filter,
          dateAdded: new Date(),
          checkedOut: IN_STORE,
          animationData: {
            destinationId: event.process,
            tileZone: 'EARTH',
            stateCode,
            cluster,
            parametricFunction: generateSpiralLerp(clusterPosition),
            parametricCurrentValue: 0,
            parametricTargetValue: 0,
            phase: 'ENTRANCE',
            debutTime: new Date(new Date(event.timestamp).getTime() + debutBufferSeconds * 1000),
            transition: {
              entranceWaypoint: getEntranceWaypoint(),
              startTime: new Date(),
              transitioningTo: "EARTH",
              jitter: 0,
            }
          },
        };
        state.asinTiles.push(tileData);

        const culled = cullOldTiles(state.asinTiles, state);

        const updatedClusters = new Set([cluster.name, ...culled.map((c) => c.animationData.cluster!.name)]);

        updatedClusters.forEach((clusterName) => {
          if (clusterName) {
            arrangeEarthClusterTiles(state.asinTiles, clusterName);
          }
        });

        return {
          asinTiles: [...state.asinTiles],
        };
      });
    },
  }
})

export default useStore